import React, { useState, useEffect } from 'react';
import { logger, LogLevel, LogCategory } from '../utils/logger';

interface LogViewerProps {
  className?: string;
}

export function LogViewer({ className = "" }: LogViewerProps) {
  const [logs, setLogs] = useState(logger.getLogs());
  const [filterLevel, setFilterLevel] = useState<LogLevel | null>(null);
  const [filterCategory, setFilterCategory] = useState<LogCategory | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Update logs every second
  useEffect(() => {
    const interval = setInterval(() => {
      setLogs(logger.getLogs());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    if (autoScroll) {
      const logContainer = document.getElementById('log-container');
      if (logContainer) {
        logContainer.scrollTop = logContainer.scrollHeight;
      }
    }
  }, [logs, autoScroll]);

  const filteredLogs = logs.filter(log => {
    if (filterLevel !== null && log.level !== filterLevel) return false;
    if (filterCategory !== null && log.category !== filterCategory) return false;
    return true;
  });

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case LogLevel.DEBUG: return 'text-gray-500';
      case LogLevel.INFO: return 'text-blue-600';
      case LogLevel.WARN: return 'text-orange-600';
      case LogLevel.ERROR: return 'text-red-600';
      default: return 'text-gray-500';
    }
  };

  const getCategoryColor = (category: LogCategory) => {
    switch (category) {
      case LogCategory.WALLET: return 'bg-blue-100 text-blue-800';
      case LogCategory.CONTRACT: return 'bg-green-100 text-green-800';
      case LogCategory.NFC: return 'bg-purple-100 text-purple-800';
      case LogCategory.API: return 'bg-yellow-100 text-yellow-800';
      case LogCategory.UI: return 'bg-gray-100 text-gray-800';
      case LogCategory.ERROR: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const exportLogs = () => {
    const dataStr = logger.exportLogs();
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `logs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Only show in development
  if (process.env.REACT_APP_NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div className={`bg-white rounded-lg shadow-lg p-4 ${className}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Frontend Logs</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => logger.clearLogs()}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Clear
          </button>
          <button
            onClick={exportLogs}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={filterLevel !== null ? filterLevel : ''}
          onChange={(e) => setFilterLevel(e.target.value ? Number(e.target.value) : null)}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
        >
          <option value="">All Levels</option>
          <option value={LogLevel.DEBUG}>Debug</option>
          <option value={LogLevel.INFO}>Info</option>
          <option value={LogLevel.WARN}>Warn</option>
          <option value={LogLevel.ERROR}>Error</option>
        </select>

        <select
          value={filterCategory || ''}
          onChange={(e) => setFilterCategory(e.target.value as LogCategory || null)}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
        >
          <option value="">All Categories</option>
          {Object.values(LogCategory).map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>

        <label className="flex items-center text-sm">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="mr-1"
          />
          Auto Scroll
        </label>
      </div>

      {/* Logs */}
      <div
        id="log-container"
        className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs h-96 overflow-y-auto"
      >
        {filteredLogs.length === 0 ? (
          <div className="text-gray-500">No logs to display</div>
        ) : (
          filteredLogs.map((log, index) => (
            <div key={index} className="mb-1">
              <span className="text-gray-400">[{log.timestamp}]</span>
              <span className={`ml-2 ${getLevelColor(log.level)}`}>
                [{LogLevel[log.level]}]
              </span>
              <span className={`ml-2 px-1 rounded text-xs ${getCategoryColor(log.category)}`}>
                {log.category}
              </span>
              <span className="ml-2">{log.message}</span>
              {log.data && (
                <div className="ml-4 text-gray-300">
                  {JSON.stringify(log.data, null, 2)}
                </div>
              )}
              {log.error && (
                <div className="ml-4 text-red-400">
                  Error: {log.error.message}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="mt-2 text-xs text-gray-500">
        Showing {filteredLogs.length} of {logs.length} logs
      </div>
    </div>
  );
}

export default LogViewer;
